import { useEffect, useRef, useState } from 'react';
import AiOverviewService from '../Services/AiOverviewService';
import { streamForBooking } from '../Services/OverviewStreamService';

// The five states the panel renders. Nothing else is reachable.
export const OVERVIEW_IDLE = 'idle';
export const OVERVIEW_LOADING = 'loading';
export const OVERVIEW_STREAMING = 'streaming';
export const OVERVIEW_READY = 'ready';
export const OVERVIEW_FAILED = 'failed';

/**
 * The AI overview for ONE booking, fetched once per modal open.
 *
 * TWO PATHS, AND THE SPLIT IS THE WHOLE FEATURE
 * ---------------------------------------------
 * 1. A STORED briefing. One `GET /bookings/ai-overview` — an indexed Mongo
 *    read, no claim, no model — and the finished briefing is set in a
 *    single state update. `OVERVIEW_STREAMING` is never entered,
 *    `streamForBooking` is never called, and the panel paints the whole
 *    thing on its first render.
 *
 * 2. NO stored briefing. Only then is the runtime invoked, and the text
 *    appears as the model writes it.
 *
 * THAT ORDER IS A REQUIREMENT, NOT AN OPTIMISATION. Revealing cached text
 * progressively would be an animation pretending to think: the words
 * already exist, and dribbling them out would tell the operator a model is
 * working when nothing is. So the only way text can arrive progressively
 * here is for it to be arriving progressively from Bedrock. There is no
 * typewriter, no timer and no interpolation anywhere in this file — the
 * prose grows only when an `overview.delta` event lands.
 *
 * Fire-and-forget by design: the request is started and never awaited by the
 * caller, so the modal paints its details immediately and the briefing drops
 * in when it resolves. The guard below is a ref keyed on the BOOKING rather
 * than an effect dependency list:
 *
 *   The details modal's state churns constantly — the tab changes, the
 *   payment ledger arrives, the booking is re-fetched fresh on open, trip
 *   claims land. An effect keyed off any of that would bill a new generation
 *   for each one. `askedForRef` makes the request idempotent per booking for
 *   the life of the mount, so re-renders cannot multiply it.
 *
 * `enabled` is how a caller says "this is an existing booking being opened":
 * pass false on a new-booking flow and no request is ever made. A missing
 * `bookingId` is the same answer — there is nothing to brief on.
 *
 * Failure is SILENT. This panel is not essential, so a fault must not write
 * to the console: an unmocked late log from a non-essential on-mount request
 * is what turns a green suite into "Cannot log after tests are done" and an
 * exit code of 1 in this repo.
 */
export default function useBookingAiOverview(bookingId, { enabled = true } = {}) {
  const [state, setState] = useState(OVERVIEW_IDLE);
  const [overview, setOverview] = useState(null);
  // The code-authored fields, which arrive BEFORE the prose on a
  // generation: `gaps` and `sources` are computed during fact-gathering, so
  // the honesty mechanism is on screen while the briefing is still being
  // written rather than after it.
  const [facts, setFacts] = useState(null);
  // Prose so far. Only ever appended to by a delta event.
  const [partial, setPartial] = useState('');

  // The booking a request has already gone out for.
  const askedForRef = useRef(null);
  // False once unmounted, so a response that outlives the modal is dropped
  // instead of setting state on a component nobody is looking at.
  const liveRef = useRef(true);
  const abortRef = useRef(null);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
      // A generation the operator walked away from is abandoned rather than
      // left to finish into a component that is gone. The server-side claim
      // and commit are unaffected: whoever is generating still commits, so
      // the next open is served from cache.
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !bookingId) return;
    if (askedForRef.current === bookingId) return;
    askedForRef.current = bookingId;

    setState(OVERVIEW_LOADING);
    setOverview(null);
    setFacts(null);
    setPartial('');

    const mine = () => liveRef.current && askedForRef.current === bookingId;

    // `summary` is the load-bearing field — a briefing with no prose is
    // nothing to show. The `status` string is deliberately NOT the gate:
    // hiding a perfectly good overview because a status word differed
    // would be the worse failure of the two.
    const usable = (value) => Boolean(
      value && typeof value.summary === 'string' && value.summary.trim(),
    );

    const settle = (value) => {
      if (!mine()) return;
      if (usable(value)) {
        setOverview(value);
        setState(OVERVIEW_READY);
      } else {
        setState(OVERVIEW_FAILED);
      }
    };

    const stream = () => {
      const controller = new AbortController();
      abortRef.current = controller;
      let streamed = false;

      return streamForBooking(bookingId, {
        signal: controller.signal,
        onEvent: (event) => {
          if (!mine()) return;
          switch (event?.type) {
            case 'overview.cached':
              // Generated by somebody else, or already stored under a
              // fingerprint we had not seen. Painted whole — nothing to
              // animate, because nothing is being written.
              settle(event.overview);
              break;
            case 'overview.facts':
              setFacts(event.facts || null);
              setState(OVERVIEW_STREAMING);
              break;
            case 'overview.delta':
              streamed = true;
              setState(OVERVIEW_STREAMING);
              setPartial((text) => text + (event.content || ''));
              break;
            case 'overview.complete':
              // The STORED object, not the text we accumulated — so what is
              // on screen at the end is byte-for-byte what the next opener
              // will be served from cache.
              settle(event.overview);
              break;
            case 'error':
              setState(OVERVIEW_FAILED);
              break;
            default:
              break;
          }
        },
      }).then(() => {
        // A stream that ended without ever completing leaves the partial
        // text on screen as evidence of what happened, not as an answer:
        // nothing was stored, and the next open regenerates.
        if (mine() && streamed) {
          setState((current) => (current === OVERVIEW_STREAMING ? OVERVIEW_FAILED : current));
        }
      });
    };

    // Not awaited: that is the whole point.
    AiOverviewService.getForBooking(bookingId)
      .then((payload) => {
        if (!mine()) return undefined;
        if (usable(payload?.overview)) {
          // THE CACHED PATH. One state update, straight to ready. No
          // streaming state is entered and the runtime is never invoked.
          setOverview(payload.overview);
          setState(OVERVIEW_READY);
          return undefined;
        }
        return stream();
      })
      .catch(() => {
        if (mine()) setState(OVERVIEW_FAILED);
      });
  }, [enabled, bookingId]);

  return {
    state, overview, facts, partial,
  };
}
